import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import imageStorage from '../utils/imageStorage';
import { Loader } from 'lucide-react';

/**
 * AuthenticatedImage - Loads images that require authentication
 * First tries local IndexedDB, then falls back to API with auth headers
 */
const AuthenticatedImage = ({ receiptId, alt, className, onError }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadImage();
  }, [receiptId]);

  const loadImage = async () => {
    if (!receiptId) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      // First try IndexedDB (fastest, works offline)
      const localImage = await imageStorage.getImage(receiptId);
      if (localImage?.imageData) {
        setImageSrc(`data:image/png;base64,${localImage.imageData}`);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.log('IndexedDB not available:', err.message);
    }

    // Fall back to API with authentication
    try {
      const response = await api.get(`/receipts/${receiptId}/image`, {
        responseType: 'blob'
      });
      
      const blobUrl = URL.createObjectURL(response.data);
      setImageSrc(blobUrl);
    } catch (err) {
      console.error('Failed to load image:', err);
      setError(true);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-white/5 ${className}`}>
        <Loader className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`flex items-center justify-center bg-white/5 text-muted-foreground ${className}`}>
        <span className="text-sm">Image unavailable</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={() => {
        setError(true);
        if (onError) onError(new Error('Image load failed'));
      }}
    />
  );
};

export default AuthenticatedImage;
